import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Translate as t } from '../../config/Translate';
import { WorkScheduleAction } from '../../actions/WorkScheduleAction';
import { getBaseSummaryParts, getTimezoneLabel } from '../../components/workschedule/workScheduleUtils';
import WorkScheduleBaseEditor from '../../components/workschedule/WorkScheduleBaseEditor';
import WorkScheduleMonthView from '../../components/workschedule/WorkScheduleMonthView';
import { useConfirmDialog } from '../../components/common/ConfirmDialog';

const DAY_KEYS = ['common.day.sun', 'common.day.mon', 'common.day.tue', 'common.day.wed', 'common.day.thu', 'common.day.fri', 'common.day.sat'];

const getSourceLabel = (source) => {
	switch (source) {
		case 'system': return t('user.workSchedule.appliedSourceSystem');
		case 'company': return t('user.workSchedule.appliedSourceCompany');
		case 'user': return t('user.workSchedule.appliedSourceUser');
		default: return '';
	}
};

const getCurrentYearMonth = () => {
	const now = new Date();
	const y = now.getFullYear();
	const m = String(now.getMonth() + 1).padStart(2, '0');
	return `${y}-${m}`;
};

const shiftMonth = (yearMonth, delta) => {
	const [y, m] = yearMonth.split('-').map(Number);
	const d = new Date(y, m - 1 + delta, 1);
	const ny = d.getFullYear();
	const nm = String(d.getMonth() + 1).padStart(2, '0');
	return `${ny}-${nm}`;
};

const MyWorkSchedule = ({ dispatch, isLoading, effectiveBase, userBase, companyTimezone, overrides, currentYearMonth, saveSuccess }) => {
	const { confirm, dialog } = useConfirmDialog();
	const [showBaseEditor, setShowBaseEditor] = useState(false);
	const [yearMonth, setYearMonth] = useState(currentYearMonth || getCurrentYearMonth());

	useEffect(() => {
		dispatch(WorkScheduleAction.GetMySchedule(yearMonth));
	}, [yearMonth]);

	useEffect(() => {
		if (saveSuccess && showBaseEditor) {
			setShowBaseEditor(false);
		}
	}, [saveSuccess]);

	const handleSaveBase = (data) => {
		dispatch(WorkScheduleAction.SaveMyBase(data, yearMonth));
	};

	const handleResetBase = () => {
		dispatch(WorkScheduleAction.ResetMyBase(yearMonth));
	};

	const handleAddOverride = (data) => {
		dispatch(WorkScheduleAction.AddOverrideRange(data, yearMonth));
	};

	const handleDeleteOverride = async (_id) => {
		if (await confirm({ message: t('common.message.confirm.delete'), danger: true })) {
			dispatch(WorkScheduleAction.DeleteOverride(_id, yearMonth));
		}
	};

	const handlePrev = () => setYearMonth(prev => shiftMonth(prev, -1));
	const handleNext = () => setYearMonth(prev => shiftMonth(prev, 1));

	const summary = getBaseSummaryParts(effectiveBase, t, DAY_KEYS);

	return (
		<div className="section mt-1">
			{dialog}
			{/* Base Schedule Summary Card */}
			<div className="card mb-2">
				<div className="card-body">
					<div className="d-flex justify-content-between align-items-start">
						<div>
							<h6 className="card-title mb-1">{t('user.workSchedule.base')}</h6>
							{effectiveBase && (
								<span className="badge bg-secondary mb-2">{getSourceLabel(effectiveBase.source)}</span>
							)}
						</div>
						<button
							className="btn btn-sm btn-outline-primary"
							onClick={() => setShowBaseEditor(true)}
						>
							<ion-icon name="create-outline"></ion-icon>
						</button>
					</div>
					{effectiveBase ? (
						<div className="small">
							{effectiveBase.timezone && (
								<div><strong>{t('user.workSchedule.timezone')}:</strong> {getTimezoneLabel(effectiveBase.timezone)}</div>
							)}
							<div><strong>{t('user.workSchedule.workDays')}:</strong> {summary.days}</div>
							<div><strong>{t('user.workSchedule.timeSlots')}:</strong> {summary.timeRange}</div>
							{summary.breakRange && (
								<div><strong>Break:</strong> {summary.breakRange}</div>
							)}
						</div>
					) : (
						<p className="text-muted small mb-0">{isLoading ? t('service.issueManager.processing') : '-'}</p>
					)}
				</div>
			</div>

			{/* Month View with Overrides */}
			<WorkScheduleMonthView
				yearMonth={yearMonth}
				overrides={overrides}
				onAdd={handleAddOverride}
				onDelete={handleDeleteOverride}
				onPrev={handlePrev}
				onNext={handleNext}
				isLoading={isLoading}
			/>

			{/* Base Editor Modal */}
			<WorkScheduleBaseEditor
				show={showBaseEditor}
				onClose={() => setShowBaseEditor(false)}
				effectiveBase={effectiveBase}
				onSave={handleSaveBase}
				onReset={handleResetBase}
				isLoading={isLoading}
				userTimezone={userBase?.timezone || ''}
				companyTimezone={companyTimezone || ''}
			/>
		</div>
	);
};

const mapState = (state) => ({
	isLoading: state.WorkScheduleReducer.isLoading,
	effectiveBase: state.WorkScheduleReducer.effectiveBase,
	userBase: state.WorkScheduleReducer.userBase,
	companyTimezone: state.WorkScheduleReducer.companyTimezone,
	overrides: state.WorkScheduleReducer.overrides,
	currentYearMonth: state.WorkScheduleReducer.currentYearMonth,
	saveSuccess: state.WorkScheduleReducer.saveSuccess,
});

export default connect(mapState)(MyWorkSchedule);
